import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { AdminUser, RoleType } from '../types';
import { adminApi } from '../api/admin';
import { teamApi } from '../api/team';
import { useAuth } from '../context/AuthContext';
import { Toast, useToast } from '../components/Toast';

const roleOf = (m: AdminUser): RoleType =>
  m.roles.includes('EMPLOYEE_ADMIN') ? 'EMPLOYEE_ADMIN' : 'EMPLOYEE';

/** Skrzynka firmowa na maile o nowych zgłoszeniach — ustawienie własnego integratora. */
const NotificationEmailCard: React.FC = () => {
  const [email, setEmail] = useState('');
  const [initial, setInitial] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    teamApi.getNotificationEmail()
      .then(({ data }) => { setEmail(data.email || ''); setInitial(data.email || ''); })
      .catch(() => setError('Nie udało się załadować ustawienia'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await teamApi.setNotificationEmail(email.trim());
      setInitial(email.trim());
      setSaved(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Nie udało się zapisać');
    } finally {
      setSaving(false);
    }
  };

  const dirty = email.trim() !== initial.trim();
  const validEmail = !email.trim() || /\S+@\S+\.\S+/.test(email.trim());

  if (loading) return null;

  return (
    <div className="card" style={{ padding: '1.25rem 1.5rem', maxWidth: 640, marginTop: '1.5rem' }}>
      <h2 style={{ fontSize: '1.0625rem', marginTop: 0, marginBottom: '0.5rem' }}>Powiadomienia mailowe</h2>
      <p className="item-meta" style={{ marginBottom: '1rem', fontSize: '0.875rem' }}>
        Adres skrzynki firmowej, na który będą wysyłane <strong>maile o nowych zgłoszeniach od klientów</strong>.
        Jeśli puste — maile pójdą do każdego pracownika osobno (na jego adres).
        Powiadomienia w aplikacji (dzwoneczek) zawsze trafiają do wszystkich pracowników.
      </p>

      <div className="form-group">
        <label className="form-label">Adres e-mail skrzynki firmowej</label>
        <input
          className="form-input"
          type="email"
          placeholder="np. serwis@twoja-firma.pl"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setSaved(false); }}
          autoComplete="off"
        />
        {!validEmail && (
          <div className="item-meta" style={{ color: 'var(--color-danger)', marginTop: '0.25rem' }}>
            Nieprawidłowy format e-maila
          </div>
        )}
      </div>

      {error && <div className="auth-error">{error}</div>}

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={save} disabled={saving || !dirty || !validEmail}>
          {saving ? 'Zapisuję…' : 'Zapisz'}
        </button>
        {saved && !dirty && (
          <span className="item-meta" style={{ color: 'var(--color-success)' }}>✓ Zapisano</span>
        )}
      </div>
    </div>
  );
};

const TeamMembers: React.FC = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalRole, setModalRole] = useState<RoleType>('EMPLOYEE');
  const [busy, setBusy] = useState(false);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [search, setSearch] = useState('');
  const { toast, showToast, dismissToast } = useToast();

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.listUsers();
      setMembers(data.filter((u) => u.roles.includes('EMPLOYEE') || u.roles.includes('EMPLOYEE_ADMIN')));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Nie udało się pobrać pracowników.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!user?.roles?.includes('EMPLOYEE_ADMIN') && !user?.roles?.includes('ADMIN')) {
    return <Navigate to="/machines" replace />;
  }

  const q = search.trim().toLowerCase();
  const visible = q
    ? members.filter((m) => `${m.firstName} ${m.lastName}`.toLowerCase().includes(q))
    : members;
  const admins = visible.filter((m) => roleOf(m) === 'EMPLOYEE_ADMIN');
  const employees = visible.filter((m) => roleOf(m) === 'EMPLOYEE');
  const selected = members.find((m) => m.id === selectedId) || null;

  const openModal = () => {
    if (!selected) return;
    setModalRole(roleOf(selected));
    setError('');
    setShowModal(true);
  };

  const toggleActive = () => {
    if (!selected) return;
    const activating = selected.approvalStatus !== 'APPROVED';
    if (!activating) {
      setError('');
      setShowDeactivate(true);
      return;
    }
    performToggle(true);
  };

  const performToggle = async (activating: boolean) => {
    if (!selected) return;
    setToggleBusy(true);
    setError('');
    try {
      await teamApi.setMemberActive(selected.id, activating);
      setShowDeactivate(false);
      setSelectedId(null);
      await load();
      showToast('success', activating
        ? `Konto ${selected.firstName} ${selected.lastName} aktywowane.`
        : `Konto ${selected.firstName} ${selected.lastName} dezaktywowane.`);
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Nie udało się zmienić statusu konta.';
      setError(msg);
      showToast('error', msg);
    } finally {
      setToggleBusy(false);
    }
  };

  const removeMember = async () => {
    if (!selected) return;
    setRemoveBusy(true);
    setError('');
    try {
      const name = `${selected.firstName} ${selected.lastName}`;
      await teamApi.removeMember(selected.id);
      setShowRemove(false);
      setSelectedId(null);
      await load();
      showToast('success', `Pracownik ${name} został usunięty z zespołu.`);
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Nie udało się usunąć pracownika.';
      setError(msg);
      showToast('error', msg);
    } finally {
      setRemoveBusy(false);
    }
  };

  const confirm = async () => {
    if (!selected) return;
    setBusy(true);
    setError('');
    try {
      await teamApi.changeRole(selected.id, modalRole);
      const label = modalRole === 'EMPLOYEE_ADMIN' ? 'Admin integratora' : 'Pracownik';
      setShowModal(false);
      setSelectedId(null);
      await load();
      showToast('success', `Zmieniono rolę: ${selected.firstName} ${selected.lastName} → ${label}.`);
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Nie udało się zmienić roli.';
      setError(msg);
      showToast('error', msg);
    } finally {
      setBusy(false);
    }
  };

  const th: React.CSSProperties = {
    textAlign: 'left',
    padding: '0.55rem 0.9rem',
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: 'var(--color-text-muted)',
    borderBottom: '2px solid var(--color-border-strong)',
  };
  const td: React.CSSProperties = {
    padding: '0.6rem 0.9rem',
    borderTop: '1px solid var(--color-border)',
    fontSize: '0.9375rem',
    wordBreak: 'break-word',
  };
  const colSep: React.CSSProperties = { borderRight: '1px solid var(--color-border)' };

  const renderGroup = (title: string, list: AdminUser[]) => (
    <section style={{ marginBottom: '1.5rem' }}>
      <h2 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>
        {title} <span className="item-meta" style={{ display: 'inline', fontSize: '0.8125rem' }}>({list.length})</span>
      </h2>
      {list.length === 0 ? (
        <div className="empty-state" style={{ padding: '1rem' }}>Brak</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ background: 'var(--color-surface-alt)' }}>
                <th style={{ ...th, ...colSep, width: 48 }} aria-label="Zaznacz" />
                <th style={{ ...th, ...colSep, width: 240 }}>Imię</th>
                <th style={{ ...th, width: 240 }}>Nazwisko</th>
              </tr>
            </thead>
            <tbody>
              {list.map((m) => {
                const self = m.id === user?.userId;
                const locked = self || !!m.founderAdmin;
                const checked = selectedId === m.id;
                return (
                  <tr
                    key={m.id}
                    onClick={() => { if (!locked) setSelectedId(checked ? null : m.id); }}
                    title={m.founderAdmin ? 'Rola nadana przez super-admina — nie podlega zmianie' : undefined}
                    style={{
                      cursor: locked ? 'default' : 'pointer',
                      background: checked ? 'var(--color-primary-light)' : undefined,
                    }}
                  >
                    <td style={{ ...td, ...colSep, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={locked}
                        onChange={(e) => setSelectedId(e.target.checked ? m.id : null)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: 16, height: 16 }}
                      />
                    </td>
                    <td style={{ ...td, ...colSep, fontWeight: 600 }}>
                      {m.firstName}
                      {m.approvalStatus === 'REJECTED' && (
                        <span className="badge badge-danger" style={{ marginLeft: '0.4rem' }}>Nieaktywny</span>
                      )}
                    </td>
                    <td style={{ ...td, fontWeight: 600 }}>{m.lastName}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Pracownicy</h1>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            className="btn btn-primary"
            disabled={!selectedId || toggleBusy}
            onClick={toggleActive}
          >
            {toggleBusy ? 'Zapisuję…'
              : selected?.approvalStatus === 'REJECTED' ? 'Aktywuj konto' : 'Dezaktywuj konto'}
          </button>
          <button className="btn btn-primary" disabled={!selectedId} onClick={openModal}>
            Zmień rolę
          </button>
          <button
            className="btn btn-danger"
            disabled={!selectedId || !!selected?.founderAdmin || removeBusy}
            title={selected?.founderAdmin ? 'Nie można usunąć pierwszego admina integratora' : undefined}
            onClick={() => { if (selected) { setError(''); setShowRemove(true); } }}
          >
            Usuń
          </button>
        </div>
      </div>

      {error && !showModal && <div className="auth-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      {!loading && members.length > 0 && (
        <input
          className="form-input"
          type="search"
          placeholder="Szukaj po imieniu lub nazwisku…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: '1rem', maxWidth: 360 }}
        />
      )}

      {loading ? (
        <div className="empty-state">Ładowanie…</div>
      ) : members.length === 0 ? (
        <div className="empty-state">Brak pracowników</div>
      ) : visible.length === 0 ? (
        <div className="empty-state">Brak pasujących pracowników</div>
      ) : (
        <>
          {renderGroup('Administratorzy', admins)}
          {renderGroup('Pracownicy', employees)}
        </>
      )}

      {user?.roles?.includes('EMPLOYEE_ADMIN') && <NotificationEmailCard />}

      {showDeactivate && selected && (
        <div className="modal-overlay" onClick={() => { if (!toggleBusy) setShowDeactivate(false); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <h2 className="modal-title">Dezaktywuj konto</h2>
            <p style={{ marginBottom: '0.75rem' }}>
              Czy na pewno chcesz dezaktywować konto{' '}
              <strong>{selected.firstName} {selected.lastName}</strong>?
            </p>
            <p className="item-meta" style={{ marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Użytkownik zostanie natychmiast wylogowany i straci dostęp do aplikacji.
            </p>
            {error && <div className="auth-error">{error}</div>}
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeactivate(false)} disabled={toggleBusy}>
                Anuluj
              </button>
              <button type="button" className="btn btn-danger" onClick={() => performToggle(false)} disabled={toggleBusy}>
                {toggleBusy ? 'Zapisuję…' : 'Dezaktywuj konto'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRemove && selected && (
        <div className="modal-overlay" onClick={() => { if (!removeBusy) setShowRemove(false); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <h2 className="modal-title">Usuń pracownika</h2>
            <p style={{ marginBottom: '0.75rem' }}>
              Czy na pewno chcesz usunąć{' '}
              <strong>{selected.firstName} {selected.lastName}</strong> z zespołu?
            </p>
            <p className="item-meta" style={{ marginBottom: '0.5rem', fontSize: '0.875rem' }}>
              Pracownik przestanie należeć do Twojego integratora i zostanie natychmiast wylogowany.
              Konto użytkownika oraz jego dotychczasowa historia pozostają nienaruszone.
            </p>
            {error && <div className="auth-error">{error}</div>}
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowRemove(false)} disabled={removeBusy}>
                Anuluj
              </button>
              <button type="button" className="btn btn-danger" onClick={removeMember} disabled={removeBusy}>
                {removeBusy ? 'Usuwam…' : 'Usuń'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && selected && (
        <div className="modal-overlay" onClick={() => { if (!busy) setShowModal(false); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <h2 className="modal-title">Zmień rolę</h2>
            <p style={{ marginBottom: '0.75rem' }}>
              Użytkownik: <strong>{selected.firstName} {selected.lastName}</strong>
            </p>
            <div className="form-group">
              <label className="form-label">Rola</label>
              <select
                className="form-select"
                value={modalRole}
                onChange={(e) => setModalRole(e.target.value as RoleType)}
                disabled={busy}
              >
                <option value="EMPLOYEE">Pracownik</option>
                <option value="EMPLOYEE_ADMIN">Admin integratora</option>
              </select>
            </div>
            {error && <div className="auth-error">{error}</div>}
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={busy}>
                Anuluj
              </button>
              <button type="button" className="btn btn-primary" onClick={confirm} disabled={busy}>
                {busy ? 'Zapisuję…' : 'Potwierdź'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default TeamMembers;
